import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { courseService } from '@/services/course.service';
import { bundleService } from '@/services/bundle.service';
import { enrollmentService } from '@/services/enrollment.service';
import { stashedInfoService } from '@/services/stashedInfo.service';
import { authService } from '@/services/auth.service';
import { errorReportingService } from '@/services/errorReporting.service';
import { discountService, DiscountValidationResponse } from '@/services/discount.service';
import DiscountCodeInput from '@/components/enrollment/DiscountCodeInput';
import userActions from '@/redux/actions/user';
import { clearEnrollmentProduct, setEnrollmentProduct } from '@/redux/actions/enrollment';
import { trackEnrollment, trackForm, trackError, trackCTA, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { metaPixel } from '@/services/metaPixel';
import { useProductPricing, formatPrice } from '@/hooks/useFeaturedPricing';

// Courses eligible for bundle upsell
const BUNDLE_UPSELL_COURSE_SLUGS = [
  'prompt-engineering-mastery',
  'ai-tools-accelerator'
];
const AI_ESSENTIALS_BUNDLE_SLUG = 'ai-essentials-bundle-prompt-engineering-mastery-ai-tools-accelerator';

// Initialize Stripe with publishable key from environment
// Handle load failures gracefully (can happen with ad blockers)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
  .catch((error) => {
    console.error('Failed to load Stripe.js:', error);
    // Report error - will be auto-resolved as expected (ad blocker behavior)
    errorReportingService.reportError({
      error_type: 'StripeLoadError',
      error_message: error?.message || 'Failed to load Stripe.js',
      additional_context: {
        'Error Source': 'Stripe Initialization',
        'Likely Cause': 'Ad blocker or content blocker preventing Stripe.js from loading'
      }
    });
    return null;
  });

interface EnrollmentFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

// Card Element styling - optimized for both web and mobile
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#e5e7eb',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px', // Must be 16px+ to prevent iOS zoom on focus
      lineHeight: '24px',
      '::placeholder': {
        color: '#9ca3af',
      },
      iconColor: '#9ca3af',
      ':-webkit-autofill': {
        color: '#e5e7eb',
      },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: false,
};

const EnrollmentForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  // Get enrollment info from Redux
  const { productId, productType } = useSelector((state: any) => state.enrollment);

  const [productInfo, setProductInfo] = useState<any>(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [productError, setProductError] = useState<string | null>(null);

  const [formData, setFormData] = useState<EnrollmentFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof EnrollmentFormData | 'card' | 'terms', string>>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  // Enrollment mode state
  const [enrollmentMode, setEnrollmentMode] = useState<'new' | 'existing' | 'authenticated'>('new');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [formStarted, setFormStarted] = useState(false);
  const enrollmentStartTimeRef = useRef<number>(Date.now());

  // Discount code state
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountValidationResponse | null>(null);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string>('');

  // Terms acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Fetch dynamic pricing from API - SINGLE SOURCE OF TRUTH
  const {
    loading: pricingLoading,
    displayPrice: apiDisplayPrice,
    originalPrice: apiOriginalPrice,
    isSaleActive,
    savingsAmount: apiSavingsAmount,
    savingsPercentage: apiSavingsPercentage,
    systemDiscountCode
  } = useProductPricing(productId || '', productType as 'course' | 'bundle');

  // Fetch bundle pricing for upsell card
  const {
    displayPrice: bundleDisplayPrice,
    originalPrice: bundleOriginalPrice,
    savingsAmount: bundleSavingsAmount,
    savingsPercentage: bundleSavingsPercentage,
    isSaleActive: bundleSaleActive
  } = useProductPricing(AI_ESSENTIALS_BUNDLE_SLUG, 'bundle');

  // Track scroll depth
  useScrollTracking('Enrollment');

  // Check if product is eligible for bundle upsell
  const isEligibleForBundleUpsell = productType === 'course' &&
    productId &&
    BUNDLE_UPSELL_COURSE_SLUGS.includes(productId);

  // Handle upgrade to bundle - preserves form data
  const handleUpgradeToBundle = () => {
    // Track bundle upgrade click
    trackCTA.click('upgrade-to-bundle', 'Upgrade to Bundle', 'enrollment-form', '/enrollment');
    // Clear any applied discount code since it was validated for the original product
    setAppliedDiscount(null);
    setAppliedDiscountCode('');
    // Update Redux state with bundle product (form data stays intact in component state)
    dispatch(setEnrollmentProduct(AI_ESSENTIALS_BUNDLE_SLUG, 'bundle') as any);
  };

  // Fetch product information when component mounts
  useEffect(() => {
    const fetchProductInfo = async () => {
      if (!productId || !productType) {
        setProductError('No product selected for enrollment');
        setLoadingProduct(false);
        return;
      }

      // Track enrollment start
      trackEnrollment.start(productId, productType === 'bundle' ? productId : null, 'direct');
      trackPageView('Enrollment', { productId, productType });

      // Meta Pixel
      metaPixel.init();
      metaPixel.pageView();
      enrollmentStartTimeRef.current = Date.now();

      try {
        setLoadingProduct(true);
        setProductError(null);

        if (productType === 'course') {
          const response = await courseService.getCourseById(productId);
          if (response.success && response.data) {
            setProductInfo(response.data);
          } else {
            setProductError(response.error || 'Failed to load course information');
            trackError.apiError('/courses/' + productId, 404, response.error || 'Course not found');
          }
        } else if (productType === 'bundle') {
          const response = await bundleService.getBundleById(productId);
          if (response.success && response.data) {
            setProductInfo(response.data);
          } else {
            setProductError(response.error || 'Failed to load bundle information');
            trackError.apiError('/bundles/' + productId, 404, response.error || 'Bundle not found');
          }
        }
      } catch (error) {
        console.error('Error fetching product info:', error);
        setProductError('An error occurred while loading product information');
        trackError.apiError('/product/' + productId, 500, 'Error loading product');
      } finally {
        setLoadingProduct(false);
      }
    };

    fetchProductInfo();
  }, [productId, productType]);

  // Check if user is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userStr = localStorage.getItem('user');

    let user = authService.getCurrentUser();

    // Fallback: if authService fails, try parsing directly
    if (!user && userStr && userStr !== 'undefined' && userStr !== 'null') {
      try {
        const parsedUser = JSON.parse(userStr);
        if (parsedUser && typeof parsedUser === 'object' && parsedUser.email) {
          user = parsedUser;
        }
      } catch (e) {
        // Invalid user data in localStorage
      }
    }

    // Check both token AND valid user data with email
    const hasValidAuth = token &&
                         token !== 'undefined' &&
                         token !== 'null' &&
                         user &&
                         user.email;

    if (hasValidAuth) {
      setEnrollmentMode('authenticated');
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
      }));
      setInfoMessage(`You're enrolling as ${user.username}`);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Track form start on first interaction
    if (!formStarted) {
      trackForm.formStart('enrollment-form', 'Enrollment Form');
      setFormStarted(true);
    }

    // Clear error when user starts typing
    if (errors[name as keyof EnrollmentFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Handle username changes with debounced availability check
    if (name === 'username') {
      // Clear existing timeout
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
      }

      // Only check if username has minimum length
      if (value.trim().length >= 3) {
        const timeout = setTimeout(async () => {
          await checkUsernameAvailability(value.trim());
        }, 500); // 500ms debounce
        setUsernameCheckTimeout(timeout);
      }
    }
  };

  // Track form field focus
  const handleFieldFocus = (fieldName: string) => {
    if (!formStarted) {
      trackForm.formStart('enrollment-form', 'Enrollment Form');
      setFormStarted(true);
    }
    trackForm.fieldFocus('enrollment-form', fieldName);
  };

  const checkUsernameAvailability = async (username: string) => {
    if (username.length < 3) return;

    setCheckingUsername(true);
    try {
      const response = await authService.checkUsernameAvailability(username);
      if (response.success && response.data) {
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
        } else {
          // Clear error if username is available
          setErrors(prev => ({ ...prev, username: '' }));
        }
      }
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setErrors(prev => ({ ...prev, card: event.error.message }));
    } else {
      setErrors(prev => ({ ...prev, card: '' }));
    }
  };

  // Handle discount code applied/removed
  const handleDiscountApplied = (discountInfo: DiscountValidationResponse | null, code: string) => {
    setAppliedDiscount(discountInfo);
    setAppliedDiscountCode(code);
  };

  const checkEmailExists = async (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return;

    setIsCheckingEmail(true);
    try {
      const response = await authService.checkEmailExists(email);
      if (response.success && response.data.exists) {
        // Existing user found
        setEnrollmentMode('existing');
        setInfoMessage("Welcome back! Please enter your password to continue.");
        setErrors(prev => ({ ...prev, email: '' }));
      } else {
        // New user
        setEnrollmentMode('new');
        setInfoMessage('');
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailBlur = async () => {
    // Track email field completion
    trackEnrollment.emailEntered(productId || '');
    trackForm.fieldComplete('enrollment-form', 'email');

    // Check if email exists (only if not authenticated)
    if (enrollmentMode !== 'authenticated') {
      await checkEmailExists(formData.email);
    }

    // Only stash info if we have all required fields filled with valid data
    const hasFirstName = formData.firstName.trim().length > 0;
    const hasLastName = formData.lastName.trim().length > 0;
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

    if (hasFirstName && hasLastName && hasValidEmail && productId && productType) {
      try {
        // Stash the enrollment information
        await stashedInfoService.createStashedInfo({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          product_id: productId,
          product_type: productType
        });
        // Silently succeed - no need to show notification to user
      } catch (error) {
        // Silently fail - this is not critical to the checkout process
        console.error('Failed to stash enrollment info:', error);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof EnrollmentFormData | 'card' | 'terms', string>> = {};

    // Personal Information (conditional based on mode)
    if (enrollmentMode !== 'authenticated') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }

    // First/Last name only required for new users
    if (enrollmentMode === 'new') {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    }

    // Username validation (only for new users)
    if (enrollmentMode === 'new') {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (formData.username.trim().length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
        newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
      } else if (errors.username === 'Username is already taken') {
        // Preserve the "username is taken" error from async check
        newErrors.username = 'Username is already taken';
      }
    }

    // Password validation (not required for authenticated users)
    if (enrollmentMode !== 'authenticated') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (enrollmentMode === 'new' && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      // Confirm password only for new users
      if (enrollmentMode === 'new' && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Terms validation (always required)
    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the Terms of Service to continue';
    }

    // Card validation (always required)
    if (!cardComplete) {
      newErrors.card = 'Please enter complete card information';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if Stripe loaded - if not, show helpful error
    if (!stripe || !elements) {
      setErrors(prev => ({
        ...prev,
        card: 'Payment system unavailable. Please disable any ad blockers and refresh the page to complete your purchase.'
      }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    // Track payment start
    const displayPrice = productInfo?.discount_price || productInfo?.full_price || 0;
    trackEnrollment.paymentStart(displayPrice, productType || 'course', productId || '');
    trackForm.formSubmit('enrollment-form', 'Enrollment Form', true);

    try {
      // Get the CardElement
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
        },
      });

      if (stripeError) {
        // Track payment failure
        trackEnrollment.paymentFailure(stripeError.message || 'Card validation failed', stripeError.code || 'stripe_error', displayPrice, productId || '');
        trackError.paymentError(stripeError.code || 'stripe_error', stripeError.message || 'Card validation failed', displayPrice);

        // Report Stripe error to backend for email alert
        errorReportingService.reportError({
          error_type: 'StripePaymentError',
          error_message: stripeError.message || 'Card validation failed',
          additional_context: {
            'Error Source': 'Stripe Payment',
            'Stripe Error Code': stripeError.code,
            'Product ID': productId,
            'Product Type': productType,
            'Product Name': productInfo?.title,
            'Amount': displayPrice,
            'User Email': formData.email
          }
        });

        setErrors(prev => ({ ...prev, card: stripeError.message || 'Card validation failed' }));
        setIsProcessing(false);
        return;
      }

      let response;

      if (enrollmentMode === 'authenticated') {
        // Use authenticated endpoint
        response = await enrollmentService.createAuthenticatedEnrollment({
          product_id: productId,
          product_type: productType,
          payment_method_id: paymentMethod?.id || '',
          discount_code: appliedDiscountCode || undefined,
        });
      } else {
        // Use regular endpoint with mode flag
        const enrollmentData: any = {
          email: formData.email,
          password: formData.password,
          product_id: productId,
          product_type: productType,
          payment_method_id: paymentMethod?.id || '',
          login_mode: enrollmentMode === 'existing',
          discount_code: appliedDiscountCode || undefined,
        };

        // Add username and name fields only for new users
        if (enrollmentMode === 'new') {
          enrollmentData.username = formData.username;
          enrollmentData.first_name = formData.firstName;
          enrollmentData.last_name = formData.lastName;
        }

        response = await enrollmentService.createEnrollment(enrollmentData);
      }

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Enrollment failed');
      }
      // Track payment success
      const transactionId = response.data.enrollment?.id || response.data.id || 'unknown';
      trackEnrollment.paymentSuccess(transactionId, displayPrice, productType || 'course', productId || '');
      trackEnrollment.complete(productId || '', response.data.user?.id || 'unknown', transactionId);

      // Store purchase data for Meta Pixel tracking on welcome page
      sessionStorage.setItem('metaPixelPurchase', JSON.stringify({
        value: displayPrice,
        currency: 'USD',
        content_ids: [productId || ''],
        content_name: productInfo?.title || productType === 'bundle' ? 'Course Bundle' : 'Course',
        content_type: 'product',
        num_items: productType === 'bundle' ? 2 : 1,
      }));

      // Store access token and user in localStorage (required for authentication)
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }

      if (response.data.user) {
        // Store user in localStorage for authentication checks
        localStorage.setItem('user', JSON.stringify(response.data.user));
        // Update Redux state with user info
        dispatch(userActions.login(response.data.user) as any);
      }

      // Clear enrollment product from Redux
      dispatch(clearEnrollmentProduct());
      // Small delay to ensure localStorage and Redux state are fully updated
      // before navigation triggers auth check
      setTimeout(() => {
        navigate('/welcome', { replace: true });
      }, 100);

    } catch (error: any) {
      console.error('Enrollment error:', error);
      const errorMessage = enrollmentMode === 'existing' && error.message?.includes('Invalid')
        ? 'Incorrect password. Please try again.'
        : error.message || 'Enrollment failed. Please try again.';

      // Track enrollment/payment failure
      trackEnrollment.paymentFailure(errorMessage, 'enrollment_error', displayPrice, productId || '');
      trackError.paymentError('enrollment_error', errorMessage, displayPrice);
      trackForm.formError('enrollment-form', 'Enrollment Form', errorMessage);

      // Report error to backend for email alert
      errorReportingService.reportError({
        error_type: 'EnrollmentError',
        error_message: errorMessage,
        stack_trace: error.stack,
        additional_context: {
          'Error Source': 'Enrollment Page',
          'Product ID': productId,
          'Product Type': productType,
          'Product Name': productInfo?.title,
          'Amount': displayPrice,
          'Enrollment Mode': enrollmentMode,
          'User Email': formData.email
        }
      });

      setErrors(prev => ({
        ...prev,
        card: errorMessage
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading state
  if (loadingProduct) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-blue-950 to-emerald-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading product information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (productError || !productInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-blue-950 to-emerald-950 flex items-center justify-center p-4">
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-8 max-w-md text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-semibold text-red-300 mb-2">Product Not Found</h2>
          <p className="text-gray-400 mb-6">{productError}</p>
          <button
            onClick={() => navigate('/courses')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Courses
          </button>
        </div>
      </div>
    );
  }

  // Calculate display price - use discount code if applied, otherwise use API pricing
  // This ensures pricing respects the admin sale toggle setting
  const originalPrice = apiOriginalPrice || productInfo.full_price;
  const displayPrice = appliedDiscount?.valid
    ? appliedDiscount.final_price
    : apiDisplayPrice || productInfo.full_price;
  const hasDiscount = appliedDiscount?.valid || (isSaleActive && apiSavingsAmount > 0);

  return (
    <>
      <Helmet>
        <title>Enroll Now - Start Your AI Learning Journey | AI Mastery Hub</title>
        <meta name="description" content="Complete your enrollment and start learning AI, machine learning. Secure checkout with Stripe. Join thousands of students today." />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-mastery-hub.com/enrollment" />
        <meta property="og:title" content="Enroll Now - Start Your AI Learning Journey" />
        <meta property="og:description" content="Complete your enrollment and start mastering AI today. Secure checkout with Stripe." />
        <meta property="og:image" content="https://ai-mastery-hub.com/enrollment-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://ai-mastery-hub.com/enrollment" />
        <meta property="twitter:title" content="Enroll Now - AI Mastery Hub" />
        <meta property="twitter:description" content="Complete your enrollment and start mastering AI today." />
        <meta property="twitter:image" content="https://ai-mastery-hub.com/enrollment-og-image.jpg" />

        {/* Additional SEO */}
        <meta name="keywords" content="enroll AI course, join AI bootcamp, AI course enrollment, machine learning signup, secure course checkout" />
        <link rel="canonical" href="https://ai-mastery-hub.com/enrollment" />
        <meta name="robots" content="noindex, nofollow" /> {/* Don't index checkout pages */}
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-black via-blue-950 to-emerald-950 py-6 sm:py-12 px-3 sm:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 mb-2">
              Complete Your Enrollment
            </h1>
            <p className="text-sm sm:text-base text-gray-400">Join thousands of users mastering AI</p>
          </div>

        {/* Product Summary Card */}
        <div className="bg-slate-900/80 backdrop-blur-sm border border-blue-500/20 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-200 mb-1">{productInfo.title}</h2>
              <p className="text-sm text-gray-400">
                {productType === 'course' ? 'Course' : 'Bundle'}
              </p>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-2xl sm:text-3xl font-bold text-blue-400">{pricingLoading ? '...' : formatPrice(displayPrice)}</div>
              {appliedDiscount?.valid ? (
                <div className="text-sm text-gray-500 line-through">{formatPrice(originalPrice)}</div>
              ) : (isSaleActive && apiSavingsAmount > 0) ? (
                <div className="text-sm text-gray-500 line-through">{formatPrice(originalPrice)}</div>
              ) : null}
              {appliedDiscount?.valid && (
                <div className="text-xs text-emerald-400 mt-1">
                  {appliedDiscount.discount_percentage}% discount applied
                </div>
              )}
              {!appliedDiscount?.valid && isSaleActive && apiSavingsAmount > 0 && (
                <div className="text-xs text-emerald-400 mt-1">
                  {apiSavingsPercentage}% sale discount
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enrollment Form */}
        <form onSubmit={handleSubmit} className="bg-slate-900/80 backdrop-blur-sm border border-blue-500/20 rounded-xl p-4 sm:p-8 shadow-2xl space-y-6 sm:space-y-8">
          {/* Info Banner */}
          {infoMessage && (
            <div className={`rounded-lg p-4 border-l-4 ${
              enrollmentMode === 'authenticated'
                ? 'bg-emerald-900/20 border-emerald-500'
                : 'bg-blue-900/20 border-blue-500'
            }`}>
              <p className="text-sm text-gray-300">{infoMessage}</p>
            </div>
          )}

          {/* Personal Information Section */}
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-blue-500/30">
              Personal Information
            </h2>

            {enrollmentMode === 'new' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="firstName" className="text-sm font-semibold text-gray-300">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                    errors.firstName ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                  }`}
                  autoComplete="given-name"
                />
                {errors.firstName && <span className="text-xs text-red-400">{errors.firstName}</span>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="lastName" className="text-sm font-semibold text-gray-300">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                    errors.lastName ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                  }`}
                  autoComplete="family-name"
                />
                {errors.lastName && <span className="text-xs text-red-400">{errors.lastName}</span>}
              </div>
            </div>
            )}

            {enrollmentMode !== 'authenticated' && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-semibold text-gray-300">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    className={`w-full px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                      errors.email ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                    }`}
                    autoComplete="email"
                  />
                  {isCheckingEmail && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                {errors.email && <span className="text-xs text-red-400">{errors.email}</span>}
              </div>
            )}

            {enrollmentMode === 'new' && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="username" className="text-sm font-semibold text-gray-300">
                  Username
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                      errors.username ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                    }`}
                    autoComplete="username"
                    placeholder="Choose a username"
                  />
                  {checkingUsername && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                {errors.username && <span className="text-xs text-red-400">{errors.username}</span>}
                {!errors.username && formData.username.length >= 3 && !checkingUsername && (
                  <span className="text-xs text-emerald-400">✓ Username is available</span>
                )}
              </div>
            )}
          </div>

          {/* Password Section */}
          {enrollmentMode !== 'authenticated' && (
            <div className="space-y-4 sm:space-y-5">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-blue-500/30">
                {enrollmentMode === 'existing' ? 'Enter Password' : 'Create Password'}
              </h2>
            
              <div className={`grid grid-cols-1 ${enrollmentMode === 'new' ? 'md:grid-cols-2' : ''} gap-4`}>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="password" className="text-sm font-semibold text-gray-300">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 pr-12 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                        errors.password ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                      }`}
                      autoComplete={enrollmentMode === 'existing' ? 'current-password' : 'new-password'}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-1"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="text-xs text-red-400">{errors.password}</span>}
                  {enrollmentMode === 'new' && (
                    <span className="text-xs text-gray-500">Must be at least 8 characters</span>
                  )}
                </div>

                {enrollmentMode === 'new' && (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-300">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 pr-12 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:bg-slate-800/80 focus:ring-2 focus:ring-blue-500/30 ${
                          errors.confirmPassword ? 'border-red-400 focus:border-red-400' : 'border-blue-500/20 focus:border-blue-500'
                        }`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-1"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="text-xs text-red-400">{errors.confirmPassword}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Information Section */}
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-blue-500/30">
              Payment Information
            </h2>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-300">
                Card Details
              </label>
              {/*
                CardElement container - Critical mobile optimizations:
                - min-h-[52px] ensures proper touch target on mobile (44px minimum + padding)
                - py-4 provides adequate vertical padding for touch
                - focus-within styles provide visual feedback
              */}
              <div
                className={`px-4 py-4 min-h-[52px] bg-slate-800/60 border-2 rounded-lg transition-all ${
                  errors.card
                    ? 'border-red-400'
                    : 'border-blue-500/20 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30'
                }`}
                style={{
                  // Ensure CardElement has proper touch handling on mobile
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <CardElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={handleCardChange}
                  onFocus={() => handleFieldFocus('card')}
                />
              </div>
              {errors.card && <span className="text-xs text-red-400">{errors.card}</span>}
              <p className="text-xs text-gray-500 mt-1">
                Powered by Stripe. Your payment information is encrypted and secure.
              </p>
            </div>

            {/* Discount Code Input */}
            {productId && productType && (
              <DiscountCodeInput
                productId={productId}
                productType={productType}
                originalPrice={originalPrice}
                onDiscountApplied={handleDiscountApplied}
                initialCode={isSaleActive && systemDiscountCode ? systemDiscountCode : undefined}
              />
            )}
          </div>

          {/* Bundle Upsell Card */}
          {isEligibleForBundleUpsell && (
            <div className="relative mt-6">
              {/* Glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 rounded-xl blur opacity-30 animate-pulse"></div>

              <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-purple-500/40 rounded-xl p-5 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2"></div>

                {/* Best Value Badge - only show when bundle has savings */}
                {bundleSavingsAmount > 0 && (
                  <div className="absolute -top-1 -right-1">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-lg">
                      SAVE {formatPrice(bundleSavingsAmount)}
                    </div>
                  </div>
                )}

                <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Icon and Text */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                      <span className="text-sm font-bold text-purple-300 uppercase tracking-wide">Upgrade to the Bundle</span>
                    </div>

                    <h4 className="text-lg font-bold text-white mb-1">
                      Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">Both Courses</span> for Just {formatPrice(bundleDisplayPrice)}
                    </h4>

                    <p className="text-sm text-gray-400 leading-relaxed">
                      Everything you need to get consistently stunning outputs from any AI tool.
                      {bundleSavingsPercentage > 0 && (
                        <span className="text-green-400 font-medium"> Save {bundleSavingsPercentage}% vs buying separately.</span>
                      )}
                    </p>

                    {/* Quick Benefits */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-300">
                        <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        15 Hours Training
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-300">
                        <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        2 Certificates
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-300">
                        <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        $3,000+ Resources
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleUpgradeToBundle}
                      className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 text-white font-bold text-sm rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30 flex items-center justify-center gap-2"
                    >
                      <span>Upgrade to Bundle</span>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                    <div className="text-center mt-2">
                      {bundleSavingsAmount > 0 && (
                        <span className="text-xs text-gray-500 line-through">{formatPrice(bundleOriginalPrice)}</span>
                      )}
                      <span className="text-xs text-green-400 ml-2 font-semibold">{formatPrice(bundleDisplayPrice)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Terms of Service Checkbox */}
          <div className="mt-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (e.target.checked && errors.terms) {
                      setErrors(prev => ({ ...prev, terms: '' }));
                    }
                  }}
                  className="sr-only peer"
                />
                <div className={"w-5 h-5 border-2 rounded transition-all flex items-center justify-center " + (errors.terms ? 'border-red-400 bg-red-900/20' : 'border-blue-500/40 bg-slate-800/60 peer-checked:bg-blue-600 peer-checked:border-blue-600 group-hover:border-blue-500/60')}>
                  {acceptedTerms && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-300 leading-relaxed">
                I have read and agree to the{' '}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
              </span>
            </label>
            {errors.terms && <span className="text-xs text-red-400 mt-1 block">{errors.terms}</span>}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col gap-3 sm:gap-4 mt-4">
            <button
              type="submit"
              className="w-full px-6 sm:px-8 py-4 sm:py-4 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-emerald-600 rounded-lg hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 min-h-[52px] active:scale-[0.98]"
              disabled={isProcessing || !stripe}
              style={{
                // Ensure proper touch handling on mobile
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                `Complete Enrollment - ${pricingLoading ? '...' : formatPrice(displayPrice)}`
              )}
            </button>

            <p className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500 m-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Your information is secure and encrypted
            </p>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

// Wrapper component to provide Stripe context
const EnrollmentPage: React.FC = () => {
  const elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'night',
    },
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <EnrollmentForm />
    </Elements>
  );
};

export default EnrollmentPage;